import { useEffect, useState, useCallback, type JSX, type ReactNode } from 'react';
import { CssBaseline } from '@mui/material';
import { type Session, type User } from '@supabase/supabase-js';
import { AuthContext, getSupabaseClient, setCachedToken, registerForceSignOut, type AuthContextValue } from '../lib/auth';
import { apiFetch } from '../lib/api-client';
import { ForcePasswordResetGate } from './ForcePasswordResetGate';
import { WebThemeProvider } from '../lib/theme-context';

interface AuthProviderProps {
  readonly children: ReactNode;
}

interface ActingSession {
  readonly company_id?: string;
  readonly company_name?: string;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [actingCompanyName, setActingCompanyName] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    void (async () => {
      let supabase;
      try {
        supabase = await getSupabaseClient();
      } catch (err) {
        console.error('[Auth] getSupabaseClient failed:', err);
        setLoading(false);
        return;
      }

      // Resolve which company this session acts on.
      //
      // Normal users: profiles.company_id.
      //
      // Superadmins: the active "view as" session, fetched from the API — NEVER
      // profiles.company_id. Some superadmin accounts carry a legacy company_id
      // and honouring it silently scoped the whole app to the wrong tenant.
      // No active session => null => ProtectedRoute sends them to the picker.
      const fetchCompanyId = async (userId: string, accessToken: string | null): Promise<void> => {
        try {
          const { data } = await supabase
            .from('profiles')
            .select('company_id, role')
            .eq('id', userId)
            .maybeSingle();
          const profile = data as { company_id: string | null; role: string | null } | null;
          setRole(profile?.role ?? null);

          if (profile?.role === 'superadmin') {
            if (!accessToken) {
              setCompanyId(null);
              setActingCompanyName(null);
              return;
            }
            // Hardcoded prod API URL — same as api-client.ts. Vite env vars
            // are baked at build time and not reliable for cross-origin paths.
            const apiUrl = 'https://api.click2gro.com.au';
            try {
              const res = await fetch(`${apiUrl}/api/v1/admin/impersonation/current`, {
                headers: { Authorization: `Bearer ${accessToken}` },
              });
              if (!res.ok) { setCompanyId(null); setActingCompanyName(null); return; }
              const text = await res.text();
              if (!text) { setCompanyId(null); setActingCompanyName(null); return; }
              const json = JSON.parse(text) as { session?: ActingSession | null };
              setCompanyId(json.session?.company_id ? String(json.session.company_id) : null);
              setActingCompanyName(json.session?.company_name ?? null);
            } catch {
              // Network error or non-JSON response (e.g. Cloudflare 503 HTML) —
              // fail closed so we never scope to the wrong company.
              setCompanyId(null);
              setActingCompanyName(null);
            }
            return;
          }

          setActingCompanyName(null);
          if (profile?.company_id) {
            setCompanyId(String(profile.company_id));
          }
        } catch (err) {
          console.error('[Auth] Failed to fetch company_id:', err);
        }
      };

      const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
        console.log('[Auth] onAuthStateChange event:', event, 'Has session:', !!newSession);

        // Update user/session state immediately
        setSession(newSession);
        setUser(newSession?.user ?? null);
        const newToken = newSession?.access_token ?? null;
        setCachedToken(newToken);
        setToken(newToken);
        if (newSession?.user) {
          void fetchCompanyId(newSession.user.id, newSession.access_token ?? null);
        } else {
          setCompanyId(null);
          setActingCompanyName(null);
          setRole(null);
        }

        // Ensure we stop loading on INITIAL_SESSION or SIGNED_IN.
        // We use a small timeout to ensure the state setter above has "taken"
        // before the ProtectedRoute re-evaluates.
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setTimeout(() => setLoading(false), 0);
        }
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          setToken(null);
          setCachedToken(null);
          setCompanyId(null);
          setActingCompanyName(null);
          setLoading(false);
        }
      });

      // Fallback: check getSession immediately
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      if (existingSession) {
        setSession(existingSession);
        setUser(existingSession.user);
        setCachedToken(existingSession.access_token ?? null);
        setToken(existingSession.access_token ?? null);
        void fetchCompanyId(existingSession.user.id, existingSession.access_token ?? null);
        setLoading(false);
      } else {
        // If no session found immediately, the onAuthStateChange will handle it.
        // We increase the timeout slightly for slow network/storage.
        setTimeout(() => {
          setLoading((prev) => {
            if (prev) console.warn('[Auth] Loading timed out after 3s');
            return false;
          });
        }, 3000);
      }

      cleanup = () => listener.subscription.unsubscribe();
    })();

    return () => cleanup?.();
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<{ error: string | null }> => {
      const trimmed = email.trim();
      if (!trimmed) return { error: 'Email is required.' };
      if (!password) return { error: 'Password is required.' };
      const supabase = await getSupabaseClient();
      const { error } = await supabase.auth.signInWithPassword({
        email: trimmed,
        password,
      });
      if (error) return { error: error.message };
      return { error: null };
    },
    [],
  );

  const signOut = useCallback(async () => {
    const supabase = await getSupabaseClient();
    // Always attempt signOut — Supabase JS clears local session regardless of
    // whether the remote call succeeds (e.g. a 403 from an already-expired token
    // still clears localStorage/cookies, which is all we need).
    await supabase.auth.signOut({ scope: 'local' });
  }, []);

  // Superadmin: begin acting on a company. The server records the choice (and
  // audits it); nothing about this session's credentials changes — only which
  // company the API and RLS resolve for it.
  const selectCompany = useCallback(async (targetCompanyId: string): Promise<{ error: string | null }> => {
    try {
      const res = await apiFetch('/api/v1/admin/impersonation/start', {
        method: 'POST',
        // eslint-disable-next-line no-restricted-syntax -- JSON request body
        body: JSON.stringify({ company_id: targetCompanyId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        return { error: body.error ?? `Could not switch to that company (HTTP ${res.status}).` };
      }
      const body = await res.json() as { company_id: string; company_name: string | null };
      setCompanyId(String(body.company_id));
      setActingCompanyName(body.company_name);
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Could not switch company.' };
    }
  }, []);

  // Superadmin: stop acting on the company. The server deletes the row, so the
  // very next request — API or direct Supabase query — is scoped to nothing.
  // There is no credential to revoke and nothing cached client-side.
  const exitCompany = useCallback(async (): Promise<void> => {
    try {
      await apiFetch('/api/v1/admin/impersonation/stop', { method: 'POST' });
    } catch (err) {
      console.error('[Auth] Failed to stop company view:', err);
    }
    setCompanyId(null);
    setActingCompanyName(null);
  }, []);

  // Register the signOut handler globally so apiFetch can trigger it on 401.
  useEffect(() => {
    registerForceSignOut(() => { void signOut(); });
  }, [signOut]);

  const value: AuthContextValue = {
    user,
    session,
    token,
    companyId,
    role,
    loading,
    actingCompanyName,
    isSuperadmin: role === 'superadmin',
    signIn,
    signOut,
    selectCompany,
    exitCompany,
  };

  // Block the app behind a forced password change when a superadmin has flagged
  // this user (app_metadata.force_password_reset). The gate clears the flag and
  // refreshes the session, after which `user` no longer carries it.
  const forcePasswordReset = !!user && user.app_metadata?.['force_password_reset'] === true;

  return (
    <AuthContext.Provider value={value}>
      {forcePasswordReset
        // Wrap the gate in the theme provider too — `children` (which normally
        // supplies it) isn't rendered while the gate is up, so without this the
        // gate falls back to MUI's default light theme.
        ? (
          <WebThemeProvider>
            <CssBaseline />
            <ForcePasswordResetGate />
          </WebThemeProvider>
        )
        : children}
    </AuthContext.Provider>
  );
}
